/**
 * AssetQuickSearch — navigation-bar autocomplete for fast asset lookup.
 *
 * Pattern: MUI Autocomplete "search as you type" (per MUI docs) — server-side
 * filtering via GraphQL, debounced 300ms, min 2 chars, client-side filtering
 * disabled (`filterOptions={(x) => x}`).
 *
 * - Select a result        → navigate to the asset detail page.
 * - Enter (free text) /Find → navigate to the asset search page with the term.
 *
 * Results are scoped to the current user's plant access on the backend.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { graphql } from '../gql';

const QUICK_SEARCH_ASSETS_QUERY = graphql(`
  query QuickSearchAssets($searchTerm: String!) {
    quickSearchAssets(searchTerm: $searchTerm) {
      id
      assetCode
      serialNumber
      modelName
      plantName
    }
  }
`);

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

interface AssetOption {
  id: number;
  assetCode: string;
  serialNumber?: string | null;
  modelName: string;
  plantName: string;
}

export function AssetQuickSearch() {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [runSearch, { data, loading }] = useLazyQuery(QUICK_SEARCH_ASSETS_QUERY);

  // Debounce: fire the query 300ms after typing settles, only at 2+ chars.
  useEffect(() => {
    const term = inputValue.trim();
    if (term.length < MIN_CHARS) return;

    const handle = setTimeout(() => {
      void runSearch({ variables: { searchTerm: term } });
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [inputValue, runSearch]);

  const options: AssetOption[] =
    inputValue.trim().length >= MIN_CHARS ? (data?.quickSearchAssets ?? []) : [];

  const goToSearchPage = useCallback(
    (term: string) => {
      const trimmed = term.trim();
      if (!trimmed) return;
      navigate(`/assets?input=${encodeURIComponent(trimmed)}`);
    },
    [navigate],
  );

  return (
    <div className="flex items-center gap-1">
      <Autocomplete<AssetOption, false, false, true>
        freeSolo
        size="small"
        sx={{ width: 360 }}
        options={options}
        loading={loading}
        // Disable built-in filtering — the server already filtered the results.
        filterOptions={(x) => x}
        inputValue={inputValue}
        onInputChange={(_event, value) => setInputValue(value)}
        getOptionLabel={(option) =>
          typeof option === 'string'
            ? option
            : [option.assetCode, option.serialNumber, option.modelName, option.plantName]
                .filter(Boolean)
                .join(' · ')
        }
        isOptionEqualToValue={(option, value) =>
          typeof option !== 'string' && typeof value !== 'string' && option.id === value.id
        }
        onChange={(_event, value) => {
          if (!value) return;
          if (typeof value === 'string') {
            // Free-text Enter → asset search page with the term.
            goToSearchPage(value);
          } else {
            // Selected a result → asset detail page.
            navigate(`/assets/${value.id}`);
          }
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <div className="flex flex-col py-0.5">
              <span className="font-medium text-foreground">{option.assetCode}</span>
              <span className="text-xs text-foreground-faded">
                {[option.serialNumber, option.modelName, option.plantName]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder="Search assets..." />
        )}
      />
      <IconButton size="small" aria-label="Find" onClick={() => goToSearchPage(inputValue)}>
        <SearchIcon fontSize="small" />
      </IconButton>
    </div>
  );
}
